import { z } from "zod";

import { baseEntityFields, customFieldsField } from "./base.js";

/**
 * Resource : famille d'entités "capacité interne planifiable".
 *
 * Une resource est une capacité dont la disponibilité limite les réservations :
 * salle, table, poste, box ou équipement clé exposé dans un agenda. Comme toute
 * famille du socle (cf. asset, le PATRON), le socle ne connaît PAS d'entité
 * concrète ("salle 12", "table du fond") : il connaît la famille générique
 * `resource` ; le vocabulaire métier est porté par le champ `type` (chaîne
 * libre déclarée par l'app : "salle", "table", "poste", "équipement").
 *
 * Une resource peut se rattacher à un lieu (`placeId`, fiche `place`) et
 * s'adosser à un bien matériel (`assetId`, fiche `asset`). Les réservations,
 * events et créneaux de disponibilité pointent VERS la resource (référence
 * portée par ces familles), la resource ne les stocke donc pas elle-même.
 *
 * Convention de patron (identique à asset) :
 *   - `resourceCoreFields`        : les champs MÉTIER seuls (sans champs standard).
 *   - `ResourceSchema`            : famille complète = champs standard + métier.
 *   - `ResourceCreateInputSchema` : ce que l'appelant fournit à la création
 *     (champs métier ; les champs standard sont remplis par le SDK). Sert à
 *     VALIDER l'entrée AVANT toute écriture Firestore.
 */

/** Champs métier de la famille resource (hors champs standard du socle). */
export const resourceCoreFields = {
  orgId: z.string(),
  /** Discriminant de sous-type déclaré par l'app (ex. "salle", "table", "poste"). */
  type: z.string().min(1),
  /** Nom d'affichage de la capacité (ex. "Salle Nord", "Table 7"). */
  name: z.string().min(1),
  /** Nombre de places / capacité d'accueil, optionnel. */
  capacity: z.number().int().optional(),
  /** Fiche `place` où se situe la resource, optionnel. */
  placeId: z.string().min(1).optional(),
  /** Fiche `asset` (bien matériel) adossée à la resource, optionnel. */
  assetId: z.string().min(1).optional(),
  /** La resource est-elle réservable en ligne par un client, optionnel. */
  bookableOnline: z.boolean().optional(),
  /** Temps de préparation entre deux réservations, en minutes, optionnel. */
  setupMinutes: z.number().int().optional(),
} as const;

/** Schéma complet de la famille resource (champs standard + champs métier). */
export const ResourceSchema = z.object({
  ...baseEntityFields,
  ...resourceCoreFields,
});

/** Type d'une resource. */
export type Resource = z.infer<typeof ResourceSchema>;

/**
 * Schéma des données fournies par l'appelant à la création.
 * Les champs standard (id, dates, auteurs, archivedAt, deletedAt, schemaVersion) sont
 * remplis par le SDK et ne sont donc PAS attendus ici.
 */
export const ResourceCreateInputSchema = z.object({
  ...resourceCoreFields,
  ...customFieldsField,
});

/** Type de l'entrée de création d'une resource. */
export type ResourceCreateInput = z.infer<typeof ResourceCreateInputSchema>;

/**
 * Schema du patch de mise a jour (update partiel).
 * `orgId` et `type` sont immuables (Security Rules) ; les champs standard
 * sont geres par le SDK : aucun des deux n'est accepte ici.
 */
export const ResourceUpdateInputSchema = z
  .object({ ...resourceCoreFields, ...customFieldsField })
  .omit({ orgId: true, type: true })
  .partial();

/** Type du patch de mise a jour d'une resource. */
export type ResourceUpdateInput = z.infer<typeof ResourceUpdateInputSchema>;
